// Admin catalog slice. Loads the full catalog (active + inactive) plus the
// active tiered pricing rules, and handles create/update/soft-delete for
// categories, products, and options, drag-and-drop reordering, and pricing
// upserts. The catalog resources share one `saving`/`saveError` pair so a
// single in-flight write covers any of them; `data` holds the four lists.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';
import { PRICING_STRATEGIES } from '../config/pricing-strategies.js';

// Maps a resource name to its REST collection path.
const PATHS = {
  categories: '/api/admin/categories',
  products: '/api/admin/products',
  options: '/api/admin/options',
};

// Shared error handling: a dead session drops the admin back to login; anything
// else becomes an actionable message for the save dialog / page.
const handleThunkError = (err, dispatch, rejectWithValue) => {
  if (err instanceof AdminAuthError) dispatch(clearAdminSession());
  return rejectWithValue(err.message);
};

export const fetchCatalog = createAsyncThunk(
  'adminCatalog/fetch',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      const [categories, products, options, pricing, includes] = await Promise.all([
        adminFetch(PATHS.categories),
        adminFetch(PATHS.products),
        adminFetch(PATHS.options),
        adminFetch('/api/admin/pricing'),
        adminFetch('/api/admin/includes'),
      ]);
      return {
        categories: categories.items,
        products: products.items,
        options: options.items,
        pricing: pricing.items,
        includes: includes.items,
      };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Create (no id) or update (id present) a row of the given resource.
export const saveCatalogItem = createAsyncThunk(
  'adminCatalog/save',
  async ({ resource, id, values }, { dispatch, rejectWithValue }) => {
    try {
      const isUpdate = Boolean(id);
      const { item } = await adminFetch(
        isUpdate ? `${PATHS[resource]}/${id}` : PATHS[resource],
        { method: isUpdate ? 'PUT' : 'POST', body: values },
      );
      return { resource, item };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

export const toggleCatalogItemActive = createAsyncThunk(
  'adminCatalog/toggleActive',
  async ({ resource, id, isActive }, { dispatch, rejectWithValue }) => {
    try {
      const { item } = await adminFetch(`${PATHS[resource]}/${id}/active`, {
        method: 'PATCH',
        body: { is_active: isActive },
      });
      return { resource, item };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Hard-delete a category. The server cascades the delete to the category's
// products, their options, and all related pricing rules, so we prune the same
// subtree from local state to keep the board/matrix in sync without a re-fetch.
export const deleteCategory = createAsyncThunk(
  'adminCatalog/deleteCategory',
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      await adminFetch(`${PATHS.categories}/${id}`, { method: 'DELETE' });
      return { id };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Upsert a pricing rule for a product OR a product option. `target` is
// { product_id } or { product_option_id }; `strategy` is the rule_type. For the
// tiered strategy pass `tiers` (4-bucket array); for flat pass `amount`.
export const savePricing = createAsyncThunk(
  'adminCatalog/savePricing',
  async ({
    target, strategy = PRICING_STRATEGIES.TIERED, tiers, amount,
  }, { dispatch, rejectWithValue }) => {
    try {
      const body = { ...target, rule_type: strategy };
      if (strategy === PRICING_STRATEGIES.FLAT) body.amount = amount;
      else if (strategy === PRICING_STRATEGIES.TIERED) body.tiers = tiers;
      // custom_quote carries no numeric payload.
      const { item } = await adminFetch('/api/admin/pricing', {
        method: 'PUT',
        body,
      });
      return { item };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Replace the full set of products that the given product auto-includes. The
// server returns the fresh flat include rows for that parent; we swap them into
// local state so the board/editor stay in sync without a re-fetch.
export const saveProductIncludes = createAsyncThunk(
  'adminCatalog/saveIncludes',
  async ({ productId, includes }, { dispatch, rejectWithValue }) => {
    try {
      const { items } = await adminFetch(`${PATHS.products}/${productId}/includes`, {
        method: 'PUT',
        body: { includes },
      });
      return { productId, items };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Persist a drag-and-drop reorder. The board updates its local lists
// optimistically before dispatching; on failure we re-fetch to resync.
export const reorderCatalog = createAsyncThunk(
  'adminCatalog/reorder',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await adminFetch('/api/admin/catalog/reorder', {
        method: 'POST',
        body: payload,
      });
      return true;
    } catch (err) {
      dispatch(fetchCatalog());
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Replace a row in its list, or append it if it's new. Keeps lists sorted the
// same way the server returns them (display_order then name).
const upsert = (list, item) => {
  const next = list.some((row) => row.id === item.id)
    ? list.map((row) => (row.id === item.id ? item : row))
    : [...list, item];
  return next.sort((a, b) => (a.display_order - b.display_order) || a.name.localeCompare(b.name));
};

// Replace the active pricing rule for a product/option target, or append it.
// Only one active rule exists per target, so we drop any rule sharing the same
// target before adding the fresh one.
const upsertPricing = (list, item) => {
  const sameTarget = (row) => row.product_id === item.product_id
    && row.product_option_id === item.product_option_id;
  return [...list.filter((row) => !sameTarget(row)), item];
};

const initialState = {
  data: {
    categories: [], products: [], options: [], pricing: [], includes: [],
  },
  loading: false,
  error: null,
  saving: false,
  saveError: null,
};

const adminCatalogSlice = createSlice({
  name: 'adminCatalog',
  initialState,
  reducers: {
    clearSaveError: (s) => { s.saveError = null; },
    // Optimistic, client-only reorder applied before the persist thunk resolves.
    // `categories` / `products` are arrays of patches mirroring the API payload.
    applyReorder: (s, a) => {
      const { categories = [], products = [] } = a.payload;
      const patch = (list, entries) => list.map((row) => {
        const change = entries.find((e) => e.id === row.id);
        return change ? { ...row, ...change } : row;
      });
      s.data.categories = patch(s.data.categories, categories)
        .sort((a1, b1) => a1.display_order - b1.display_order);
      s.data.products = patch(s.data.products, products)
        .sort((a1, b1) => a1.display_order - b1.display_order);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalog.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchCatalog.fulfilled, (s, a) => {
        s.loading = false;
        s.data = a.payload;
      })
      .addCase(fetchCatalog.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Could not load the catalog.';
      })
      .addCase(saveCatalogItem.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(saveCatalogItem.fulfilled, (s, a) => {
        s.saving = false;
        s.data[a.payload.resource] = upsert(s.data[a.payload.resource], a.payload.item);
      })
      .addCase(saveCatalogItem.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not save. Please try again.';
      })
      .addCase(savePricing.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(savePricing.fulfilled, (s, a) => {
        s.saving = false;
        s.data.pricing = upsertPricing(s.data.pricing, a.payload.item);
      })
      .addCase(savePricing.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not save pricing. Please try again.';
      })
      .addCase(saveProductIncludes.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(saveProductIncludes.fulfilled, (s, a) => {
        s.saving = false;
        // Swap in the parent's fresh include rows: drop its old rows, add new.
        const { productId, items } = a.payload;
        s.data.includes = [
          ...s.data.includes.filter((r) => r.product_id !== productId),
          ...items,
        ];
      })
      .addCase(saveProductIncludes.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not save included products. Please try again.';
      })
      .addCase(toggleCatalogItemActive.fulfilled, (s, a) => {
        s.data[a.payload.resource] = upsert(s.data[a.payload.resource], a.payload.item);
      })
      .addCase(toggleCatalogItemActive.rejected, (s, a) => {
        s.error = a.payload || 'Could not update status.';
      })
      .addCase(deleteCategory.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(deleteCategory.fulfilled, (s, a) => {
        s.saving = false;
        const categoryId = a.payload.id;
        const removedProductIds = s.data.products
          .filter((p) => p.category_id === categoryId)
          .map((p) => p.id);
        const removedOptionIds = s.data.options
          .filter((o) => removedProductIds.includes(o.product_id))
          .map((o) => o.id);
        s.data.categories = s.data.categories.filter((c) => c.id !== categoryId);
        s.data.products = s.data.products.filter((p) => p.category_id !== categoryId);
        s.data.options = s.data.options.filter((o) => !removedProductIds.includes(o.product_id));
        s.data.pricing = s.data.pricing.filter((r) => (
          !removedProductIds.includes(r.product_id)
          && !removedOptionIds.includes(r.product_option_id)
        ));
        // The DB cascades product_includes when products are removed; mirror it
        // by dropping any include row that referenced a removed product (as the
        // parent or the included one).
        s.data.includes = s.data.includes.filter((r) => (
          !removedProductIds.includes(r.product_id)
          && !removedProductIds.includes(r.included_product_id)
        ));
      })
      .addCase(deleteCategory.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not delete the category.';
      })
      .addCase(reorderCatalog.rejected, (s, a) => {
        s.error = a.payload || 'Could not save the new order.';
      });
  },
});

export const { clearSaveError, applyReorder } = adminCatalogSlice.actions;
export default adminCatalogSlice.reducer;
